export type Bilingual = {
  en: string
  fr: string
}

export const provenIntro = {
  eyebrow: {
    en: 'Proven in production',
    fr: 'Éprouvé en production',
  },
  headlineLead: {
    en: 'Not a promise.',
    fr: 'Pas une promesse.',
  },
  headlineBefore: {
    en: 'Already in ',
    fr: 'Déjà en ',
  },
  headlineAccent: {
    en: 'production.',
    fr: 'production.',
  },
  intro: {
    en: 'Since 2017, BCITI has been running digital infrastructure used every day by hundreds of thousands of residents. Identity, services, communications, payments and operations — connected in a single ecosystem.',
    fr: 'Depuis 2017, BCITI exploite une infrastructure numérique utilisée au quotidien par des centaines de milliers de citoyens. Identité, services, communications, paiements et opérations — connectés dans un même écosystème.',
  },
}

export type PrimaryResult = {
  id: string
  icon: 'people' | 'bolt'
  value: Bilingual
  label: Bilingual
  detail: Bilingual
  source: Bilingual
}

export const primaryResults: PrimaryResult[] = [
  {
    id: 'counter',
    icon: 'people',
    value: { en: '400 → 5', fr: '400 → 5' },
    label: {
      en: 'People waiting at the counter',
      fr: 'Personnes en attente au comptoir',
    },
    detail: {
      en: 'during BCITI Digital Card renewals.',
      fr: 'lors du renouvellement de la carte numérique BCITI.',
    },
    source: {
      en: 'Brossard · outcome observed in production',
      fr: 'Brossard · résultat observé en production',
    },
  },
  {
    id: 'reach',
    icon: 'bolt',
    value: { en: '130,000+', fr: 'Plus de 130 000' },
    label: {
      en: 'Residents reachable in under 30 minutes',
      fr: 'Citoyens joignables en moins de 30 minutes',
    },
    detail: {
      en: 'during an emergency communication.',
      fr: 'lors d’une communication d’urgence.',
    },
    source: {
      en: 'Outcome observed in production',
      fr: 'Résultat observé en production',
    },
  },
]

export type ScaleStat = {
  id: string
  icon: 'users' | 'message' | 'card' | 'share' | 'calendar'
  value: Bilingual
  label: Bilingual
}

export const scaleStats: ScaleStat[] = [
  {
    id: 'users',
    icon: 'users',
    value: { en: '326,000+', fr: 'Plus de 326 000' },
    label: { en: 'Registered users', fr: 'Utilisateurs enregistrés' },
  },
  {
    id: 'messages',
    icon: 'message',
    value: { en: '12.4M+', fr: 'Plus de 12,4 M' },
    label: { en: 'Notices and messages sent', fr: 'Avis et messages envoyés' },
  },
  {
    id: 'transactions',
    icon: 'card',
    value: { en: '1.2M', fr: '1,2 M' },
    label: { en: 'Transactions processed', fr: 'Transactions traitées' },
  },
  {
    id: 'connectors',
    icon: 'share',
    value: { en: '21', fr: '21' },
    label: { en: 'Connectors in production', fr: 'Connecteurs en production' },
  },
  {
    id: 'since',
    icon: 'calendar',
    value: { en: 'Since 2017', fr: 'Depuis 2017' },
    label: { en: 'In production', fr: 'En production' },
  },
]

export const infrastructure = {
  eyebrow: {
    en: 'Infrastructure already in production',
    fr: 'Une infrastructure déjà en production',
  },
  headline: {
    en: 'Years of integrations already encoded into the platform.',
    fr: 'Des années d’intégrations déjà incorporées à la plateforme.',
  },
  points: [
    {
      id: 'connectors',
      icon: 'plug' as const,
      value: { en: '21', fr: '21' },
      label: { en: 'Connectors in production', fr: 'Connecteurs en production' },
      detail: { en: '', fr: '' },
    },
    {
      id: 'protocols',
      icon: 'database' as const,
      value: { en: '6', fr: '6' },
      label: { en: 'Integration protocols', fr: 'Protocoles d’intégration' },
      detail: { en: '', fr: '' },
    },
    {
      id: 'functions',
      icon: 'cloud' as const,
      value: { en: '~106', fr: '~106' },
      label: { en: 'Azure Function Apps', fr: 'Azure Function Apps' },
      detail: { en: '(serverless-first)', fr: '(architecture sans serveur)' },
    },
    {
      id: 'agents',
      icon: 'brain' as const,
      value: { en: '4', fr: '4' },
      label: { en: 'Business AI agents', fr: 'Agents IA métier' },
      detail: {
        en: '(permits, requests, waste collection, water)',
        fr: '(permis, requêtes, collectes, eau)',
      },
    },
  ],
}

export const platformInAction = {
  eyebrow: {
    en: 'One infrastructure. Both sides of the service.',
    fr: 'Une même infrastructure. Des deux côtés du service.',
  },
  citizens: {
    label: { en: 'For residents', fr: 'Pour les citoyens' },
    headlineLead: { en: 'One identity.', fr: 'Une identité.' },
    headlineAccent: { en: 'All their services.', fr: 'Tous leurs services.' },
    items: [
      { icon: 'card' as const, text: { en: 'Digital card', fr: 'Carte numérique' } },
      { icon: 'payment' as const, text: { en: 'Payment', fr: 'Paiement' } },
      {
        icon: 'requests' as const,
        text: { en: 'Requests and permits', fr: 'Demandes et permis' },
      },
      {
        icon: 'message' as const,
        text: { en: 'Communications', fr: 'Communications' },
      },
    ],
  },
  organizations: {
    label: { en: 'For organizations', fr: 'Pour les organisations' },
    headlineLead: { en: 'One view.', fr: 'Une vue.' },
    headlineAccent: {
      en: 'All their operations.',
      fr: 'Toutes leurs opérations.',
    },
    items: [
      {
        icon: 'services' as const,
        text: { en: 'Services and requests', fr: 'Services et requêtes' },
      },
      { icon: 'chart' as const, text: { en: 'Performance', fr: 'Performance' } },
      { icon: 'bell' as const, text: { en: 'Alerts', fr: 'Alertes' } },
      {
        icon: 'report' as const,
        text: {
          en: 'Reports and analytics',
          fr: 'Rapports et analytique',
        },
      },
    ],
  },
  coreLayers: [
    { en: 'Identity', fr: 'Identité' },
    { en: 'Context', fr: 'Contexte' },
    { en: 'Intelligence', fr: 'Intelligence' },
    { en: 'Orchestration', fr: 'Orchestration' },
    { en: 'Action', fr: 'Action' },
  ],
  coreCaptionLead: {
    en: 'One platform.',
    fr: 'Une plateforme.',
  },
  coreCaptionAccent: {
    en: 'Real possibilities.',
    fr: 'De vraies possibilités.',
  },
}

export const provenConclusion = {
  lead: {
    en: 'More capacity. Less complexity.',
    fr: 'Plus de capacité. Moins de complexité.',
  },
  accent: {
    en: 'Measurable in production.',
    fr: 'Mesurable en production.',
  },
  footer: {
    en: 'Since 2017 · Identity · Data · Integrations · AI · Execution · Operations',
    fr: 'Depuis 2017 · Identité · Données · Intégrations · IA · Exécution · Opérations',
  },
}
